"use client";

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { useAuthTransition } from "@/lib/auth/hooks/useAuthTransition";
import { startAnonymousSession } from "@/lib/auth/anonymous-session";
import { createClient } from "@/lib/supabase/client";
import posthog from "posthog-js";
import { trackEvent } from "@/lib/analytics/posthog";
import { CONVERSION_LABELS, trackGoogleAdsConversion } from "@/lib/google-ads/gtag";
import { useQuickFlowContext } from "@/hooks/useQuickFlowContext";
import { useIsMobileViewport } from "@/hooks/useIsMobileViewport";
import { useConsent } from "@/lib/consent";
import { CHAT_STORAGE_KEY } from "@/hooks/useSalesChat";
import type { strings } from "@/lib/strings";
type Translations = typeof strings;
import { ANFRAGE_STEP_PROMPTS, ANGEBOT_STEP_PROMPTS } from "@/components/chat/widget-prompts";
type WidgetState = "closed" | "open" | "minimized";

/** Delay before showing the widget on first page load (ms) */
const APPEARANCE_DELAY = 5000;

/** Play the pop sound — call on user gesture (click) so it always works. */
function playPopSound() {
  try {
    const audio = new Audio("/sounds/pop.mp3");
    audio.volume = 0.7;
    audio.play().catch(() => {});
  } catch {
    // Audio not supported
  }
}

/**
 * True when a previous conversation is persisted. Client-only by contract —
 * never call this from a render path that also runs on the server.
 */
function hasSavedConversation(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const saved = localStorage.getItem(CHAT_STORAGE_KEY);
    if (!saved) return false;
    const parsed = JSON.parse(saved);
    return parsed?.messages?.length > 0;
  } catch {
    return false;
  }
}
export interface WidgetVisibility {
  // Core state
  state: WidgetState;
  visible: boolean;
  panelMounted: boolean;
  anonAuthReady: boolean;

  // Derived visibility flags
  isOnFormPage: boolean;
  showContextualCard: boolean;
  showFormCircle: boolean;
  showOriginalInvitation: boolean;
  showCompactCircle: boolean;

  // Contextual card data
  stepPrompt: string | null;
  stepKey: string | undefined;

  // Position helpers
  formBottom: string;
  defaultBottom: string;

  // PostHog
  posthogDistinctId: string | undefined;

  // Actions
  handleOpen: () => void;
  handleClose: () => void;
  handleMinimize: () => void;
  handleDismissInvitation: (e: React.MouseEvent) => void;
  handleDismissContextual: (e: React.MouseEvent) => void;
}
export function useWidgetVisibility(t: Translations,
// A page that already offers its readers a better next step opens on the
// compact circle: the chat stays one click away, but no card pops up.
{
  suppressInvitation = false
}: {
  suppressInvitation?: boolean;
} = {}): WidgetVisibility {
  const {
    isAuthenticated,
    user,
    isLoading,
    isLoginModalOpen
  } = useAuth();
  const authTransition = useAuthTransition();
  const quickFlowContext = useQuickFlowContext();
  const {
    showBanner: cookieBannerVisible
  } = useConsent();
  const isMobile = useIsMobileViewport();
  const [state, setState] = useState<WidgetState>("closed");
  const [panelMounted, setPanelMounted] = useState(false);

  // ── Anonymous auth: ensure a Supabase session exists before sending messages ──
  const [anonAuthReady, setAnonAuthReady] = useState(!!user);
  const isOpen = state === "open";
  useEffect(() => {
    if (isLoading || authTransition || isLoginModalOpen) {
      setAnonAuthReady(false);
      return;
    }
    if (user) {
      setAnonAuthReady(true);
      return;
    }
    setAnonAuthReady(false);
    if (!isOpen) return;
    let cancelled = false;
    const supabase = createClient();
    startAnonymousSession(supabase).then(({
      error,
      data
    }) => {
      if (error) {
        console.error("[ChatWidget] Anonymous sign-in failed:", error.message);
      }
      if (!cancelled) setAnonAuthReady(!error && !!data?.user);
    }).catch(error => console.error("[ChatWidget] Anonymous sign-in failed:", error));
    return () => {
      cancelled = true;
    };
  }, [user, isOpen, isLoading, authTransition, isLoginModalOpen]);

  // ── PostHog distinct_id ──
  const posthogDistinctId = useMemo(() => {
    if (typeof window === "undefined") return undefined;
    try {
      return posthog.get_distinct_id?.();
    } catch {
      return undefined;
    }
    // Re-compute when auth state changes (login/logout swaps the distinct ID)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isAuthenticated triggers re-read of distinct_id
  }, [isAuthenticated]);

  // ── Has the user engaged? ──
  // Server-safe default. Reading localStorage in a lazy initialiser ran during
  // SSR too, where localStorage does not exist: the server rendered the
  // invitation while a returning visitor's FIRST client render produced the
  // compact circle, and React threw "Hydration failed …" pointing at the widget
  // button. The persisted value is adopted after mount instead.
  const [hasEngaged, setHasEngaged] = useState(false);

  // ── Contextual card dismiss tracking ──
  const [cardDismissedStep, setCardDismissedStep] = useState<string | null>(null);

  // ── Appearance delay ──
  const [visible, setVisible] = useState(false);

  // Adopt the persisted engagement once we are on the client. `visible` is set
  // here as well so a returning visitor keeps their immediate circle instead of
  // waiting out APPEARANCE_DELAY; because `visible` is false for the first
  // render, ChatWidget paints nothing before this runs, so the invitation card
  // never flashes on the way to the circle.
  useEffect(() => {
    if (!hasSavedConversation()) return;
    setHasEngaged(true);
    setVisible(true);
  }, []);
  useEffect(() => {
    if (hasEngaged) {
      setPanelMounted(true);
    }
  }, [hasEngaged]);
  useEffect(() => {
    if (visible) return;
    if (quickFlowContext) {
      setVisible(true);
      return;
    }
    const timer = setTimeout(() => {
      setVisible(true);
    }, APPEARANCE_DELAY);
    return () => clearTimeout(timer);
  }, [visible, quickFlowContext]);

  // ── Handlers ──
  const hasFiredOpenConversionRef = useRef(false);
  const handleOpen = useCallback(() => {
    playPopSound();
    setState("open");
    setHasEngaged(true);
    if (!panelMounted) setPanelMounted(true);
    // Google Ads micro-conversion: first Klara open (ref guards against double-fire)
    if (!hasFiredOpenConversionRef.current && CONVERSION_LABELS.klaraOpen) {
      hasFiredOpenConversionRef.current = true;
      trackGoogleAdsConversion(CONVERSION_LABELS.klaraOpen);
    }
    trackEvent("chatbot_opened", {
      page_url: typeof window !== "undefined" ? window.location.pathname : "",
      user_type: isAuthenticated ? "authenticated" : "anonymous"
    });
  }, [panelMounted, isAuthenticated]);

  // Allow external components (e.g. KlaraHelpLink) to open the chat via custom event
  useEffect(() => {
    const handler = () => handleOpen();
    window.addEventListener("klara:open", handler);
    return () => window.removeEventListener("klara:open", handler);
  }, [handleOpen]);
  const handleClose = useCallback(() => {
    setState("closed");
    trackEvent("chatbot_closed", {
      user_type: isAuthenticated ? "authenticated" : "anonymous"
    });
  }, [isAuthenticated]);
  const handleMinimize = useCallback(() => {
    setState("minimized");
  }, []);
  const handleDismissInvitation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setHasEngaged(true);
    trackEvent("chatbot_invitation_dismissed", {
      user_type: isAuthenticated ? "authenticated" : "anonymous"
    });
  }, [isAuthenticated]);
  const handleDismissContextual = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (quickFlowContext) {
      setCardDismissedStep(quickFlowContext.step);
    }
    trackEvent("chatbot_contextual_dismissed", {
      step: quickFlowContext?.step,
      flow: quickFlowContext?.flow
    });
  }, [quickFlowContext]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && state === "open") {
        handleMinimize();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [state, handleMinimize]);

  // ── Derived visibility flags ──
  const isOnFormPage = !!quickFlowContext;
  const isCardDismissed = isOnFormPage && cardDismissedStep === quickFlowContext?.step;
  const stepPrompt = quickFlowContext ? (quickFlowContext.flow === "schnellanfrage" ? ANFRAGE_STEP_PROMPTS[quickFlowContext.step] : ANGEBOT_STEP_PROMPTS[quickFlowContext.step]) ?? t.chat?.contextualFallback ?? "Kann ich Ihnen helfen?" : null;
  const isPreviewStep = quickFlowContext?.step === "preview";
  // Hide the launcher on the contact step: the submit CTA ("Angebote
  // kostenfrei anfordern" / "Angebot jetzt einstellen") sits at the
  // bottom-right of the viewport on mobile, exactly where Klara renders,
  // causing accidental chat opens during form submission. Klara reappears
  // automatically when the user navigates back to an earlier step.
  const isOnContactStep = quickFlowContext?.step === "contact";
  // EXP-007 / HYP-009 A1+A2: on mobile during funnel steps, OR whenever the
  // cookie banner is stacking with the widget on a funnel step, collapse the
  // big contextual card to a reachable circle so the primary CTA stays clear.
  const suppressContextualOnFormPage = isOnFormPage && (isMobile || cookieBannerVisible);
  const showContextualCard = state !== "open" && isOnFormPage && !isOnContactStep && !isCardDismissed && !isPreviewStep && !suppressContextualOnFormPage;
  const showFormCircle = state !== "open" && isOnFormPage && !isOnContactStep && (isCardDismissed || isPreviewStep || suppressContextualOnFormPage);
  const showOriginalInvitation = state !== "open" && !isOnFormPage && !hasEngaged && !suppressInvitation;
  const showCompactCircle = state !== "open" && !isOnFormPage && (hasEngaged || suppressInvitation);
  const formBottom = "bottom-6 right-4 sm:right-6";
  const defaultBottom = cookieBannerVisible ? "bottom-28 right-6" : "bottom-6 right-6";
  return {
    state,
    visible,
    panelMounted,
    anonAuthReady,
    isOnFormPage,
    showContextualCard,
    showFormCircle,
    showOriginalInvitation,
    showCompactCircle,
    stepPrompt,
    stepKey: quickFlowContext?.step,
    formBottom,
    defaultBottom,
    posthogDistinctId,
    handleOpen,
    handleClose,
    handleMinimize,
    handleDismissInvitation,
    handleDismissContextual
  };
}